import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { wallPostsApi, WallPost } from '../../../api/wallPostsApi';
import { Post } from '../../profile';
import CreatePost from '../../profile/ui/CreatePost';
import SkeletonPost from '../../profile/ui/SkeletonPost';
import EditPostModal from '../../profile/ui/EditPostModal';
import { BottomSheet } from '../../../shared/ui';
import styles from './ObjectWallTab.module.css';

interface ObjectWallTabProps {
  siteId: number;
  canPost?: boolean; // показывать кнопку публикации (только для участников)
}

// Стена объекта: все публикации по объекту от всех участников (исполнители, дизайнеры, заказчики)
const ObjectWallTab: React.FC<ObjectWallTabProps> = ({ siteId, canPost = true }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [editingPost, setEditingPost] = useState<WallPost | null>(null);

  const loadPosts = useCallback(async () => {
    setIsLoading(true);
    try {
      const sitePosts = await wallPostsApi.getSitePosts(siteId, 100, 0);
      setPosts(sitePosts);
    } catch (error) {
      console.error('Ошибка загрузки постов объекта:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleLike = async (postId: number) => {
    try {
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const wasLiked = post.isLiked;
          return {
            ...post,
            isLiked: !wasLiked,
            likes: wasLiked ? post.likes - 1 : post.likes + 1
          };
        }
        return post;
      }));

      await wallPostsApi.toggleLike(postId);
    } catch (error) {
      console.error('Ошибка при лайке поста:', error);
      loadPosts();
    }
  };

  const handleDeletePost = async (postId: number) => {
    try {
      await wallPostsApi.deletePost(postId);
      setPosts(prev => prev.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Ошибка при удалении поста:', error);
    }
  };

  const handleCreatePost = async (content: string, images?: File[], postSiteId?: number | null) => {
    const newPost = await wallPostsApi.createPost({
      content,
      siteId: postSiteId ?? siteId,
      images: images || [],
    });

    setShowCreatePost(false);
    setPosts(prev => [newPost, ...prev]);
  };

  return (
    <div className={styles.wall}>
      {canPost && (
        <button
          type="button"
          className={styles.createButton}
          onClick={() => setShowCreatePost(true)}
        >
          <i className="ri-add-box-line" aria-hidden />
          Опубликовать
        </button>
      )}

      {isLoading ? (
        <>
          <SkeletonPost />
          <SkeletonPost />
        </>
      ) : posts.length === 0 ? (
        <div className={styles.emptyWall}>
          <i className="ri-article-line"></i>
          <p>По объекту пока нет публикаций</p>
          <span>Здесь собирается всё, что публикует команда объекта</span>
        </div>
      ) : (
        <div className={styles.postsList}>
          {posts.map(post => (
            <Post
              key={post.id}
              post={post}
              onLike={handleLike}
              onDelete={handleDeletePost}
              onEdit={p => setEditingPost(p)}
              currentUserId={user?.id || 0}
            />
          ))}
        </div>
      )}

      <BottomSheet
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        title="Создать новый пост"
        size="large"
      >
        <CreatePost
          preselectedSiteId={siteId}
          onCreatePost={async (content, images, postSiteId) => {
            try {
              await handleCreatePost(content, images, postSiteId);
            } catch (error) {
              console.error('Ошибка при создании поста:', error);
            }
          }}
          onCancel={() => setShowCreatePost(false)}
        />
      </BottomSheet>
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onUpdated={updated => {
            setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingPost(null);
          }}
        />
      )}
    </div>
  );
};

export default ObjectWallTab;
